import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth, hasAdminPanelAccess } from './AuthContext';
import { policyService } from '../api';
import type { PendingPolicyViewModel } from '../api/types/policy';
import PolicyAcceptanceModal from '../components/Policy/PolicyAcceptanceModal';
import { NotificationTypeId } from '../utils/notificationMeta';

export interface PolicyAcceptanceNotificationSource {
  id: string;
  typeId: number;
  contextData?: Record<string, string> | null;
}

interface PolicyAcceptanceTarget {
  policyId: string;
  policySlug?: string | null;
  notificationId?: string;
  requireAcceptance?: boolean;
  prefetchedPolicy?: PendingPolicyViewModel | null;
  onAccepted?: () => void;
}

interface PolicyAcceptanceContextValue {
  openFromNotification: (
    notification: PolicyAcceptanceNotificationSource,
    onAccepted?: () => void,
  ) => boolean;
  refreshPendingPolicies: () => Promise<void>;
  pendingCount: number;
}

const PolicyAcceptanceContext = createContext<PolicyAcceptanceContextValue | undefined>(undefined);

export function usePolicyAcceptance() {
  const ctx = useContext(PolicyAcceptanceContext);
  if (!ctx) {
    throw new Error('usePolicyAcceptance yalnızca PolicyAcceptanceProvider içinde kullanılabilir.');
  }
  return ctx;
}

interface PolicyAcceptanceProviderProps {
  children: ReactNode;
}

export function PolicyAcceptanceProvider({ children }: PolicyAcceptanceProviderProps) {
  const { isAuthenticated, user } = useAuth();
  const [activeTarget, setActiveTarget] = useState<PolicyAcceptanceTarget | null>(null);
  const [pendingQueue, setPendingQueue] = useState<PendingPolicyViewModel[]>([]);
  const gateCheckedForUserIdRef = useRef<string | null>(null);

  const showNextFromQueue = useCallback((queue: PendingPolicyViewModel[]) => {
    const [next, ...rest] = queue;
    if (!next) {
      setActiveTarget(null);
      setPendingQueue([]);
      return;
    }

    setPendingQueue(rest);
    setActiveTarget({
      policyId: String(next.id),
      policySlug: next.slug ?? null,
      requireAcceptance: true,
      prefetchedPolicy: next,
    });
  }, []);

  const refreshPendingPolicies = useCallback(async () => {
    if (!isAuthenticated || hasAdminPanelAccess(user)) {
      setPendingQueue([]);
      setActiveTarget(null);
      gateCheckedForUserIdRef.current = null;
      return;
    }

    try {
      const result = await policyService.getPendingPolicies();
      if (!result.isSuccess || !result.data?.length) {
        setPendingQueue([]);
        if (activeTarget?.requireAcceptance) {
          setActiveTarget(null);
        }
        return;
      }

      if (!activeTarget) {
        showNextFromQueue(result.data);
      } else {
        setPendingQueue(result.data.filter((p) => String(p.id) !== activeTarget.policyId));
      }
    } catch {
      /* non-critical */
    }
  }, [activeTarget, isAuthenticated, showNextFromQueue, user]);

  useEffect(() => {
    if (!isAuthenticated || !user?.id || hasAdminPanelAccess(user)) {
      gateCheckedForUserIdRef.current = null;
      setPendingQueue([]);
      setActiveTarget(null);
      return;
    }

    if (gateCheckedForUserIdRef.current === user.id) return;
    gateCheckedForUserIdRef.current = user.id;

    void (async () => {
      try {
        const result = await policyService.getPendingPolicies();
        if (result.isSuccess && result.data?.length) {
          showNextFromQueue(result.data);
        }
      } catch {
        /* non-critical */
      }
    })();
  }, [isAuthenticated, showNextFromQueue, user?.id, user]);

  const openFromNotification = useCallback(
    (notification: PolicyAcceptanceNotificationSource, onAccepted?: () => void) => {
      if (hasAdminPanelAccess(user)) return false;
      if (notification.typeId !== NotificationTypeId.RuleUpdate) return false;

      const policyId = notification.contextData?.policyId;
      if (!policyId) return false;

      setPendingQueue([]);
      setActiveTarget({
        policyId,
        policySlug: notification.contextData?.policySlug ?? null,
        notificationId: notification.id,
        requireAcceptance: false,
        onAccepted,
      });
      return true;
    },
    [user],
  );

  const handleAccepted = useCallback(async () => {
    activeTarget?.onAccepted?.();

    if (activeTarget?.requireAcceptance) {
      if (pendingQueue.length > 0) {
        showNextFromQueue(pendingQueue);
        return;
      }

      try {
        const result = await policyService.getPendingPolicies();
        if (result.isSuccess && result.data?.length) {
          showNextFromQueue(result.data);
          return;
        }
      } catch {
        /* fall through */
      }
    }

    setActiveTarget(null);
  }, [activeTarget, pendingQueue, showNextFromQueue]);

  const handleClose = useCallback(() => {
    if (activeTarget?.requireAcceptance) return;
    setActiveTarget(null);
  }, [activeTarget?.requireAcceptance]);

  const value: PolicyAcceptanceContextValue = {
    openFromNotification,
    refreshPendingPolicies,
    pendingCount: pendingQueue.length + (activeTarget?.requireAcceptance ? 1 : 0),
  };

  return (
    <PolicyAcceptanceContext.Provider value={value}>
      {children}
      {activeTarget && (
        <PolicyAcceptanceModal
          policyId={activeTarget.policyId}
          policySlug={activeTarget.policySlug}
          prefetchedPolicy={activeTarget.prefetchedPolicy ?? undefined}
          requireAcceptance={activeTarget.requireAcceptance}
          onAccepted={handleAccepted}
          onClose={handleClose}
        />
      )}
    </PolicyAcceptanceContext.Provider>
  );
}

export default PolicyAcceptanceContext;
